/**
 * What a set-menu addon's quantity MEANS, in one place.
 *
 * A row on a set line carries a count so the line stays editable in one spot,
 * but the wire contract is repeat entries: the backend prices per entry
 * (`resolveOrderAddons` + `sumSetLine`) and the kitchen payload collapses them
 * back into "name ×N". Every price the customer is shown has to expand the same
 * way the bill does, so the floor (never below one), the rounding (whole units)
 * and the expansion live here rather than being restated at each call.
 *
 * Restated is what it was: the same expression sat in the two qr pages, the
 * cashier's builder, the cart key and the picker dialog, all of them deciding
 * how much money a line is worth.
 */

/** One row's units: at least one, whole, whatever the stored value looks like. */
export const unitsOf = (row?: { quantity?: unknown } | null): number =>
  Math.max(1, Math.floor(Number(row?.quantity) || 1));

/**
 * A configured ceiling, or `null` for "no ceiling" — the group's `max_choice`
 * and an option's `max_quantity` both read through here.
 *
 * Anything that is not a usable cap collapses to `null` rather than to 0: an
 * absent field, a null off the API, a 0 written before the admin form rejected
 * them. A 0 read as a cap would disable the `+` on every group configured
 * before the fields existed.
 *
 * Mirrors the backend's `setMenuPricing.normalizeCap`, which is what actually
 * enforces this — the stepper only stops the diner from building a line the
 * order would refuse.
 */
export const capOf = (value: unknown): number | null => {
  const cap = Math.floor(Number(value));
  return Number.isFinite(cap) && cap > 0 ? cap : null;
};

/**
 * Rows to priced entries: one entry per unit, so a quantity of N contributes
 * its gross N times. The entries are the rows themselves — callers that need
 * the count STRIPPED (the order payload) pass `dropQuantity`.
 */
export const expandByQuantity = <T extends { quantity?: unknown }>(
  rows: T[] = [],
  { dropQuantity = false }: { dropQuantity?: boolean } = {}
): T[] =>
  (rows || []).flatMap((row) => {
    const { quantity, ...rest } = row as T & { quantity?: unknown };
    const entry = (dropQuantity ? rest : row) as T;
    return Array.from({ length: unitsOf(row) }, () =>
      dropQuantity ? ({ ...entry } as T) : entry
    );
  });
